import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useSpring, animated } from 'react-spring'
import { PATH } from '../../../../data/router_path'
import FormHeader from './FormHeader'
import { useNavigate } from 'react-router-dom'
import FormField from '../../../../components/function/form/FormField'
import { textFieldData } from '../../../../data/formFieldData'
import { MODE } from '../../../../data/queryFactories'
import { facultyService } from '../../../../service/FacultyService'
import { bookCollectionService } from '../../../../service/BookCollectionService'
import FileInput from '../../../../components/function/FileInput'
import Select from '../../../../components/function/filters/Select'
import CornerConfirmModal from '../../../../components/ui/CornerConfirmModal'

async function getFacultyOptions() {
  const { message, success } = await facultyService.getAll()
  if (success) {
    return (message?.content ?? [])?.map((faculty) => ({
      value: faculty.uuid,
      label: faculty.name,
    }))
  }
  return null
}

async function getBookCollectionOptions() {
  const { message, success } = await bookCollectionService.getAll()
  if (success) {
    return (message?.content ?? [])?.map((collection) => ({
      value: collection.uuid,
      label: collection.name,
    }))
  }
  return null
}
const nowDay = new Date().getFullYear()
const createUploadBookSchema = (mode) =>
  z
    .object({
      title: z
        .string()
        .min(1, 'Hãy điền Nhan đề')
        .max(255, 'Độ dài không được vượt quá 255 ký tự'),
      author: z
        .string()
        .min(1, 'Hãy điền Tác giả')
        .max(255, 'Độ dài không được vượt quá 255 ký tự'),
      publisher: z
        .string()
        .min(1, 'Hãy điền Nhà xuất bản')
        .max(255, 'Độ dài không được vượt quá 255 ký tự'),
      publishYear: z.coerce
        .number()
        .min(1000, 'Năm xuất bản phải từ 1000 trở lên')
        .max(nowDay, 'Năm xuất bản không được vượt quá năm hiện tại!'),
      facultyUuid: z.string().min(1, 'Hãy chọn Khoa/Viện'),
      subject: z.string().max(255, 'Độ dài không được vượt quá 255 ký tự'),
      isbn: z
        .string()
        .max(255, 'Độ dài không được vượt quá 255 ký tự')
        .optional(),
      price: z.coerce.number(),
      description: z
        .string()
        .max(1000, 'Độ dài không được vượt quá 1000 ký tự'),
      pdfFile: z.any().optional(),
      imageFile: z.any().optional(),
      bookType: z.enum(['Sách thường', 'Sách mượn trả']).default('Sách thường'),
      numOfBorrow: z.coerce.number().optional().nullable(),
      timeOfBorrow: z.coerce.number().optional().nullable(),
      bookCollections: z.array(z.string()),
    })
    .refine(
      (data) => {
        if (data.bookType === 'Sách mượn trả') {
          return typeof data.timeOfBorrow === 'number' && data.timeOfBorrow > 0
        }
        return true
      },
      {
        message: 'Hãy điền Thời gian mượn trả tối đa',
        path: ['timeOfBorrow'],
      }
    )
    .refine(
      (data) => {
        if (data.bookType === 'Sách mượn trả') {
          return typeof data.numOfBorrow === 'number' && data.numOfBorrow > 0
        }
        return true
      },
      {
        message: 'Hãy điền Số lượng mượn trả tối đa',
        path: ['numOfBorrow'],
      }
    )

export const uploadBookSchema = createUploadBookSchema

export default function BookForm({
  mode,
  initForm,
  initPdf,
  initCover,
  initPdfInfo,
  submitHandler,
  confirmTitle = 'Xác nhận thêm sách?',
  facultyOptions: externalFacultyOptions,
  bookCollectionOptions: externalBookCollectionOptions,
}) {
  const navigate = useNavigate()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [formFields, setFormFields] = useState(textFieldData)
  const [bookType, setBookType] = useState(initForm?.bookType || 'Sách thường')
  const borrowContentRef = useRef(null)

  const borrowContentHeight = borrowContentRef.current
    ? borrowContentRef.current.scrollHeight
    : 0

  const borrowAnimation = useSpring({
    height: bookType === 'Sách mượn trả' ? borrowContentHeight : 0,
    opacity: bookType === 'Sách mượn trả' ? 1 : 0,
    overflow: 'hidden',
  })

  useEffect(() => {
    if (borrowContentRef.current) {
      borrowAnimation.height.start(
        bookType === 'Sách mượn trả' ? borrowContentRef.current.scrollHeight : 0
      )
    }
  }, [bookType, borrowAnimation])

  const {
    watch,
    register,
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createUploadBookSchema(mode)),
    defaultValues: {
      title: '',
      author: '',
      publisher: 'NXB Đại học Kinh tế Quốc dân',
      bookCollections: [],
      publishYear: new Date().getFullYear(),
      facultyUuid: '',
      subject: '',
      isbn: '',
      price: 0,
      description: '',
      pdfFile: initPdf,
      pdfInfo: initPdfInfo,
      imageFile: initCover,
      bookType: initForm?.bookType || 'Sách thường',
      timeOfBorrow: initForm?.timeOfBorrow || null,
      numOfBorrow: initForm?.numOfBorrow || null,
      normalBook: (initForm?.bookType || 'Sách thường') === 'Sách thường',
      borrowBook: (initForm?.bookType || 'Sách thường') === 'Sách mượn trả',
      ...initForm,
    },
  })
  const formValues = watch()
  // Auto-open borrow section when borrow values exist (after formValues is ready)
  useEffect(() => {
    const timeVal = formValues?.timeOfBorrow
    const numVal = formValues?.numOfBorrow
    const hasBorrowValues =
      (timeVal !== undefined && timeVal !== null && timeVal !== '') ||
      (numVal !== undefined && numVal !== null && numVal !== '')
    if (hasBorrowValues && bookType !== 'Sách mượn trả') {
      setBookType('Sách mượn trả')
      setValue('borrowBook', true)
      setValue('normalBook', false)
      setValue('bookType', 'Sách mượn trả')
    }
  }, [formValues, bookType, setValue])
  const validateForm = useCallback(() => {
    const hasErrors = Object.keys(errors).length > 0
    const hasFieldErrors = !Object.entries(formValues).every(([key, value]) => {
      if (key === 'imageFile' || key === 'isbn') return true
      if (key === 'price') return true
      if (key === 'bookCollections') {
        return Array.isArray(value) && value.length > 0
      }
      if (key === 'pdfFile') {
        return mode === MODE.EDIT || (value && value !== null)
      }
      if (key === 'timeOfBorrow' || key === 'numOfBorrow') {
        if (bookType === 'Sách mượn trả') {
          return typeof value === 'number' && value > 0
        }
        return true
      }
      return value && value.toString().trim() !== ''
    })

    return !hasErrors && !hasFieldErrors
  }, [formValues, errors, bookType, mode, initForm])
  const loadSelectOptions = useCallback(async () => {
    let facultyOptions = externalFacultyOptions
    let bookCollectionOptions = externalBookCollectionOptions

    if (!facultyOptions) {
      facultyOptions = await getFacultyOptions()
    }
    if (!bookCollectionOptions) {
      bookCollectionOptions = await getBookCollectionOptions()
    }

    if (facultyOptions && bookCollectionOptions) {
      setFormFields((prevFields) =>
        prevFields.map((field) => {
          if (field.name === 'facultyUuid') {
            return { ...field, options: facultyOptions }
          }
          if (field.name === 'bookCollections') {
            return { ...field, options: bookCollectionOptions }
          }
          return field
        })
      )
    }
  }, [externalFacultyOptions, externalBookCollectionOptions])

  useEffect(() => {
    loadSelectOptions()
  }, [loadSelectOptions])

  useEffect(() => {
    if (initForm && mode === MODE.EDIT) {
      const bookCollectionsField = formFields.find(
        (f) => f.name === 'bookCollections'
      )
      // map book collection name for selected values
      let processedBookCollections = initForm.bookCollections
      if (initForm.bookCollections && bookCollectionsField?.options) {
        processedBookCollections = initForm.bookCollections.map(
          (collectionName) => {
            const matchingOption = bookCollectionsField.options.find(
              (option) => option.label === collectionName
            )
            return matchingOption?.value || collectionName
          }
        )
      }

      const setFormValues = async () => {
        Object.entries(initForm).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            const finalValue =
              key === 'bookCollections' ? processedBookCollections : value
            setValue(key, finalValue)
          }
        })
      }
      setFormValues()
    }
  }, [initForm, mode, setValue, formFields])

  useEffect(() => {
    setIsDisabled(!validateForm())
  }, [formValues, validateForm])

  const pdfFile = watch('pdfFile')
  const pdfInfo = watch('pdfInfo')
  const imageFile = watch('imageFile')

  const handlePdfChange = (file, info) => {
    setValue('pdfFile', file)
    setValue('pdfInfo', info)
  }
  const handleImageChange = (file) => {
    setValue('imageFile', file)
  }
  const handleRemovePdf = () => {
    setValue('pdfFile', null)
    setValue('pdfInfo', null)
  }

  const handleRemoveImage = () => {
    setValue('imageFile', null)
  }

  const handleBookTypeChange = (name, value) => {
    if (value) {
      setValue('normalBook', name === 'normalBook')
      setValue('borrowBook', name === 'borrowBook')
      setBookType(name === 'normalBook' ? 'Sách thường' : 'Sách mượn trả')
      setValue(
        'bookType',
        name === 'normalBook' ? 'Sách thường' : 'Sách mượn trả'
      )

      if (name === 'normalBook') {
        setValue('timeOfBorrow', null)
        setValue('numOfBorrow', null)
      }
    }
  }

  const onSubmit = async (formData) => {

    const processedFormData = {
      ...formData,
      pdfInfo,
      timeOfBorrow:
        typeof formData.timeOfBorrow === 'number'
          ? formData.timeOfBorrow * 3600
          : null,
      numOfBorrow:
        typeof formData.numOfBorrow === 'number' ? formData.numOfBorrow : null,
      description:
        formData.description === '' ? formData?.title : formData?.description,
      subject: formData?.subject === '' ? 'Chung' : formData?.subject,
    }
    submitHandler(pdfFile, imageFile, processedFormData)
  }

  const getBookInfo = () => {
    switch (mode) {
      case MODE.CREATE:
        return {
          title: `Thêm sách`,
          actionName: 'Thêm sách',
        }
      case MODE.EDIT:
        return {
          title: `Chỉnh sửa sách`,
          actionName: 'Cập nhật',
        }
      default:
        return { title: '', actionName: '' }
    }
  }
  const { title, actionName } = getBookInfo()

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormHeader
          title={title}
          handleCancel={() => navigate(PATH.BOOK.LIST)}
          onAccept={() => setShowConfirmation(true)}
          actionName={actionName}
          disableSubmit={isDisabled}
        />
        <div className="mt-4 grid grid-cols-10 gap-1">
          <div className="col-span-2">
            <FileInput
              mode={mode}
              acceptedFileTypes={['application/pdf']}
              acceptedImageType={[
                'image/jpeg',
                'image/png',
                'image/jpg',
                'image/webp',
              ]}
              file={pdfFile}
              fileInfo={pdfInfo}
              onFileChange={handlePdfChange}
              onRemoveFile={handleRemovePdf}
              image={imageFile}
              onImageChange={handleImageChange}
              onImageRemove={handleRemoveImage}
            />
          </div>

          <div className="col-span-8 pl-8">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <div
                  className={`focus:shadow-outline mb-4 flex min-h-[2.8rem] w-full appearance-none items-center rounded-md border border-gray-400 px-2 font-bold leading-tight text-gray-700 shadow focus:outline-none ${
                    mode === MODE.EDIT
                      ? 'cursor-not-allowed bg-gray-100'
                      : 'cursor-pointer bg-white'
                  }`}
                  onClick={
                    mode !== MODE.EDIT
                      ? () => handleBookTypeChange('normalBook', true)
                      : undefined
                  }
                >
                  <Select
                    control={control}
                    name="normalBook"
                    title="Sách thường"
                    onChangeHandler={
                      mode !== MODE.EDIT ? handleBookTypeChange : undefined
                    }
                    disabled={mode === MODE.EDIT}
                  />
                </div>

                <div
                  className={`focus:shadow-outline flex w-full appearance-none flex-col rounded-md border border-gray-400 px-2 font-bold leading-tight text-gray-700 shadow focus:outline-none ${
                    mode === MODE.EDIT && bookType !== 'Sách mượn trả'
                      ? 'cursor-not-allowed bg-gray-100'
                      : 'cursor-pointer bg-white'
                  }`}
                  onClick={
                    mode !== MODE.EDIT
                      ? () => handleBookTypeChange('borrowBook', true)
                      : undefined
                  }
                >
                  <div className="flex min-h-[2.8rem] items-center">
                    <Select
                      control={control}
                      name="borrowBook"
                      title="Sách Mượn trả"
                      onChangeHandler={
                        mode !== MODE.EDIT ? handleBookTypeChange : undefined
                      }
                      disabled={
                        mode === MODE.EDIT && bookType !== 'Sách mượn trả'
                      }
                    />
                  </div>
                  <animated.div style={borrowAnimation}>
                    <div ref={borrowContentRef}>
                      {bookType === 'Sách mượn trả' && (
                        <>
                          <div className="flex items-center border-gray-400 py-2">
                            <label className="mr-2 whitespace-nowrap">
                              Thời gian mượn trả tối đa:{' '}
                              <span className="text-red-accent">*</span>
                            </label>
                            <div className="flex flex-col">
                              <input
                                type="number"
                                className={`w-20 rounded-md border p-2 ${
                                  errors.timeOfBorrow
                                    ? 'border-red-500'
                                    : 'border-gray-400'
                                } ${mode === MODE.EDIT && bookType !== 'Sách mượn trả' ? 'cursor-not-allowed bg-gray-100' : ''}`}
                                min="1"
                                max="99999"
                                disabled={
                                  mode === MODE.EDIT &&
                                  bookType !== 'Sách mượn trả'
                                }
                                {...register('timeOfBorrow', {
                                  valueAsNumber: true,
                                })}
                              />
                              {errors.timeOfBorrow && (
                                <span className="mt-1 text-xs text-red-500">
                                  {errors.timeOfBorrow.message}
                                </span>
                              )}
                            </div>
                            <span className="ml-2">giờ</span>
                          </div>

                          <div className="flex items-center border-gray-400 py-2">
                            <label className="mr-2 whitespace-nowrap">
                              Số lượng mượn trả tối đa:{' '}
                              <span className="text-red-accent">*</span>
                            </label>
                            <div className="flex flex-col">
                              <input
                                type="number"
                                className={`w-20 rounded-md border p-2 ${
                                  errors.numOfBorrow
                                    ? 'border-red-500'
                                    : 'border-gray-400'
                                } ${mode === MODE.EDIT && bookType !== 'Sách mượn trả' ? 'cursor-not-allowed bg-gray-100' : ''}`}
                                min="1"
                                max="99999"
                                disabled={
                                  mode === MODE.EDIT &&
                                  bookType !== 'Sách mượn trả'
                                }
                                {...register('numOfBorrow', {
                                  valueAsNumber: true,
                                })}
                              />
                              {errors.numOfBorrow && (
                                <span className="mt-1 text-xs text-red-500">
                                  {errors.numOfBorrow.message}
                                </span>
                              )}
                            </div>
                            <span className="ml-2">lượt</span>
                          </div>
                        </>
                      )}
                    </div>
                  </animated.div>
                </div>
              </div>

              {formFields
                .filter(
                  (field) =>
                    field.name !== 'timeOfBorrow' &&
                    field.name !== 'numOfBorrow' &&
                    field.name !== 'categoryUuid'
                )
                .map((field) => {
                  return (
                    <div
                      className={`${
                        field.name === 'description' || field.name === 'title'
                          ? 'col-span-2'
                          : 'col-span-1'
                      } min-w-0`}
                      key={field.name}
                    >
                      <FormField
                        key={field.name}
                        type={field.type}
                        {...field}
                        register={register}
                        errors={errors}
                        control={control}
                        placeholder={field?.placeholder}
                        options={field?.options}
                        size={field?.length}
                        isTextArea={field.name === 'description'}
                      />
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </form>
      {showConfirmation && (
        <CornerConfirmModal
          title={confirmTitle}
          content=""
          onSubmit={() => {
            handleSubmit(onSubmit)()
          }}
          onClose={() => setShowConfirmation(false)}
          icon="ph:question"
          iconColor="text-info"
          titleColor="text-info"
          confirmButtonVariant="primary"
        />
      )}
    </>
  )
}
